/**
 * RoutingCategoryIconPicker - Seletor de ícone Lucide para categorias
 *
 * Lista curada de ícones Lucide relevantes para categorias de ações
 * rápidas/macros, com campo de busca. O nome selecionado é salvo na
 * coluna routing_rule_categories.icon.
 *
 * CHANGELOG:
 *   2026-06-23 v1.0.0 - Versão inicial.
 */

import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RoutingCategoryIcon } from './RoutingCategoryIcon';

// Lista curada de ícones Lucide considerados úteis para categorias.
// Mantida explícita para evitar carregar o pacote inteiro no UI.
const ICON_OPTIONS = [
  'Zap', 'Star', 'Heart', 'Flame', 'Bolt', 'Sparkles', 'Crown',
  'Tag', 'Tags', 'Bookmark', 'Pin', 'Pinned', 'Flag', 'Hash',
  'MessageSquare', 'MessageCircle', 'Mail', 'MailOpen', 'Send', 'Reply',
  'Phone', 'PhoneCall', 'Headphones', 'Mic', 'Megaphone',
  'User', 'Users', 'UserPlus', 'UserCheck', 'UserCog',
  'ShoppingCart', 'ShoppingBag', 'CreditCard', 'DollarSign', 'Receipt', 'Wallet',
  'Briefcase', 'Building', 'Building2', 'Store', 'Package',
  'Calendar', 'CalendarCheck', 'Clock', 'Timer', 'AlarmClock',
  'CheckCircle', 'CheckCircle2', 'XCircle', 'AlertCircle', 'AlertTriangle', 'Info',
  'ThumbsUp', 'ThumbsDown', 'Smile', 'Frown',
  'Rocket', 'Target', 'Trophy', 'Award', 'Medal', 'Gift',
  'Truck', 'Plane', 'Car', 'Map', 'MapPin', 'Globe', 'Compass',
  'Settings', 'Wrench', 'Tool', 'Cog', 'Sliders',
  'FileText', 'File', 'Folder', 'FolderOpen', 'Archive', 'Inbox',
  'BarChart', 'BarChart3', 'PieChart', 'LineChart', 'TrendingUp', 'TrendingDown',
  'Lightbulb', 'BookOpen', 'GraduationCap', 'Coffee',
  'Lock', 'Unlock', 'Shield', 'Key', 'Eye', 'EyeOff',
  'Filter', 'Search', 'Layers', 'Grid', 'List',
  'Play', 'Pause', 'StopCircle', 'FastForward', 'Repeat',
  'Image', 'Video', 'Music', 'Camera', 'Mic2',
  'Bot', 'Cpu', 'Database', 'Cloud', 'Wifi',
];

interface RoutingCategoryIconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  color?: string;
  disabled?: boolean;
}

/**
 * Popover com grade de ícones Lucide selecionáveis.
 */
export function RoutingCategoryIconPicker({
  value,
  onChange,
  color,
  disabled,
}: RoutingCategoryIconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filtra a grade pela busca
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ICON_OPTIONS;
    return ICON_OPTIONS.filter((name) => name.toLowerCase().includes(q));
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="h-10 gap-2 justify-between min-w-[140px]"
        >
          <span className="flex items-center gap-2">
            <RoutingCategoryIcon name={value} color={color} size={16} />
            <span className="text-xs text-muted-foreground">{value}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar ícone..."
            className="pl-8 h-8 text-xs"
            autoFocus
          />
        </div>
        <ScrollArea className="h-56">
          <div className="grid grid-cols-7 gap-1 pr-2">
            {filtered.map((name) => (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                className={cn(
                  'h-8 w-8 rounded-md flex items-center justify-center border transition-colors',
                  value === name
                    ? 'border-primary bg-primary/10'
                    : 'border-transparent hover:bg-muted'
                )}
              >
                <RoutingCategoryIcon name={name} color={color} size={16} />
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-7 py-6 text-center text-xs text-muted-foreground">
                Nenhum ícone encontrado
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
